import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DateTime } from 'luxon';

const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const dob = DateTime.fromISO(dateOfBirth);
    if (!dob.isValid) return null;
    const age = Math.floor(DateTime.now().diff(dob, 'years').years);
    return age >= 0 ? age : null;
};

const getColumnValue = (person, columnValue, personCustomFields) => {
    if (columnValue === 'phone') return person.Phones?.map(p => p.phoneNumber).join(', ') || '-';
    if (columnValue === 'email') return person.email || '-';
    if (columnValue === 'gender') return person.gender || '-';
    if (columnValue === 'age') {
        const age = person.calculatedAge !== undefined ? person.calculatedAge : calculateAge(person.dateOfBirth);
        return age !== null ? `${age}` : '-';
    }
    if (columnValue === 'role') return person.Roles?.map(r => r.name).join(', ') || '-';
    
    if (columnValue.startsWith('cf_')) {
        const cfId = columnValue.replace('cf_', '');
        const value = person.customFieldValues?.[cfId];
        if (value === null || value === undefined) return '-';
        if (typeof value === 'object') return value.name || '-';
        return String(value);
    }
    return '-';
};

const getColumnLabel = (columnValue, personCustomFields) => {
    if (columnValue === 'phone') return 'Phone';
    if (columnValue === 'email') return 'Email';
    if (columnValue === 'gender') return 'Gender';
    if (columnValue === 'age') return 'Age';
    if (columnValue === 'role') return 'Role';
    
    if (columnValue.startsWith('cf_')) {
        const cfId = columnValue.replace('cf_', '');
        const cf = personCustomFields?.find(f => f.id === cfId);
        return cf?.name || 'Custom Field';
    }
    return columnValue;
};

export const generatePDFForCheckIn = async ({ groupedData, locationName, title, eventInstance, organization, layout, selectedColumns = [], personCustomFields = [] }) => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter'
    });

    const margin = 15;
    let currentY = margin;

    const addHeader = (groupName = null, memberCount = 0) => {
        const headerStartY = currentY;
        
        doc.setFillColor(245, 247, 250);
        doc.roundedRect(margin, headerStartY, doc.internal.pageSize.getWidth() - margin * 2, 32, 3, 3, 'F');
        
        if (organization?.logoPublicUrl) {
            try {
                doc.setFillColor(50, 55, 65);
                doc.roundedRect(margin + 5, headerStartY + 4, 24, 24, 2, 2, 'F');
                doc.addImage(organization.logoPublicUrl, 'PNG', margin + 7, headerStartY + 6, 20, 20);
            } catch {
                // Logo failed to load, continue without it
            }
        }

        const titleX = organization?.logoPublicUrl ? margin + 35 : margin + 8;
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(33, 37, 41);
        doc.text(title, titleX, headerStartY + 12);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        
        let subtitleParts = [eventInstance?.Event?.name || 'Event'];
        if (eventInstance?.startTime) {
            subtitleParts.push(DateTime.fromISO(eventInstance.startTime).toLocaleString(DateTime.DATETIME_MED));
        }
        if (locationName) {
            subtitleParts.push(locationName);
        }
        doc.text(subtitleParts.join('  •  '), titleX, headerStartY + 20);

        if (groupName) {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(66, 139, 202);
            doc.text(`${groupName} (${memberCount})`, titleX, headerStartY + 28);
        }

        doc.setTextColor(0);
        currentY = headerStartY + 38;
    };

    const isFullPage = layout === 'Full Page';
    const pageHeight = doc.internal.pageSize.getHeight();

    groupedData.forEach(([groupName, members], groupIndex) => {
        if (members.length === 0) return;

        const estimatedTableHeight = (members.length + 1) * 10 + 50;
        const needsNewPage = isFullPage || (groupIndex > 0 && currentY + Math.min(estimatedTableHeight, 100) > pageHeight - margin);

        if (needsNewPage && groupIndex > 0) {
            doc.addPage();
            currentY = margin;
        }

        addHeader(groupName, members.length);

        // Build dynamic headers based on selectedColumns
        const headers = ['Name'];
        selectedColumns.forEach(col => {
            headers.push(getColumnLabel(col, personCustomFields));
        });
        headers.push('In', 'Out');

        const tableData = members.map(person => {
            const row = [`${person.firstName || ''} ${person.lastName || ''}`.trim()];
            selectedColumns.forEach(col => {
                row.push(getColumnValue(person, col, personCustomFields));
            });
            row.push('', ''); // In and Out checkboxes
            return row;
        });

        // Calculate column widths dynamically
        const totalWidth = 180; // Available width in mm
        const checkboxWidth = 22;
        const nameWidth = 55;
        const remainingWidth = totalWidth - nameWidth - (checkboxWidth * 2);
        const dataColumnWidth = selectedColumns.length > 0 ? remainingWidth / selectedColumns.length : 0;

        const columnStyles = {
            0: { cellWidth: nameWidth }
        };
        selectedColumns.forEach((_, idx) => {
            columnStyles[idx + 1] = { cellWidth: dataColumnWidth };
        });
        const inColIndex = selectedColumns.length + 1;
        const outColIndex = selectedColumns.length + 2;
        columnStyles[inColIndex] = { cellWidth: checkboxWidth, halign: 'center' };
        columnStyles[outColIndex] = { cellWidth: checkboxWidth, halign: 'center' };

        autoTable(doc, {
            startY: currentY,
            head: [headers],
            body: tableData,
            theme: 'grid',
            styles: {
                fontSize: 9,
                cellPadding: 4,
                lineColor: [220, 220, 220],
                lineWidth: 0.1
            },
            headStyles: {
                fillColor: [66, 139, 202],
                textColor: 255,
                fontStyle: 'bold',
                halign: 'center'
            },
            columnStyles,
            didDrawCell: (data) => {
                if (data.section === 'body' && (data.column.index === inColIndex || data.column.index === outColIndex)) {
                    const cellX = data.cell.x + (data.cell.width / 2) - 2.5;
                    const cellY = data.cell.y + (data.cell.height / 2) - 2.5;
                    doc.setDrawColor(150);
                    doc.setLineWidth(0.3);
                    doc.rect(cellX, cellY, 5, 5);
                }
            },
            margin: { left: margin, right: margin }
        });

        currentY = doc.lastAutoTable.finalY + (isFullPage ? 0 : 12);
    });

    doc.autoPrint();
    doc.save(`${title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
};
